import { useEffect, useRef, useState } from "react";
import type { GroupInfo } from "../../shared/api-contract";
import { useViewer } from "../../state/ViewerProvider";
import { notice, useDomainState, useUi } from "../../state/editor/useEditor";
import { Icon } from "../kit";
import { useBasicEngine } from "./basic/useBasicEngine";
import { metalFill } from "./basic/rail/ui";

/** Stones worth offering a customer at the counter, as named swatches. */
const STONES: { id: string; label: string; fill: string }[] = [
  { id: "diamondClear", label: "Diamond", fill: "radial-gradient(circle at 35% 30%, #ffffff 0, #eef3f7 30%, #b9c3cc 65%, #7c8793 100%)" },
  { id: "ruby", label: "Ruby", fill: "radial-gradient(circle at 35% 30%, #ffc2cc 0, #e0314f 32%, #9d0f2b 68%, #560717 100%)" },
  { id: "sapphire", label: "Sapphire", fill: "radial-gradient(circle at 35% 30%, #cfe0ff 0, #3a64d8 34%, #1a3596 68%, #0b1b55 100%)" },
  { id: "emerald", label: "Emerald", fill: "radial-gradient(circle at 35% 30%, #c9f4dc 0, #22a466 32%, #0f6b3f 68%, #063a22 100%)" },
];

/**
 * Present mode — for showing a piece to a customer at the counter.
 *
 * The page goes full screen, every panel steps aside (the studio's own chrome
 * and the top bar, via `data-present` on <body>), the ring turns slowly, and
 * one row of named metals and stones stays so the customer can see it in rose
 * gold or with a sapphire without anyone opening a panel. Esc (or the browser
 * leaving full screen) brings everything back.
 *
 * ⚠️ Nothing moves in the DOM: the iframe's parent is never touched (a
 * re-parent kills every RPC — see EditorShell), only visibility changes.
 */
export function PresentMode() {
  const [ui, patchUi] = useUi();
  const { client, catalogs } = useViewer();
  const engine = useBasicEngine();
  const groups = (useDomainState("groups") as GroupInfo[] | undefined) ?? [];
  const on = ui.present;
  const entered = useRef(false);
  // Named, not guessed: White Gold, Platinum and Silver are three near-equal
  // greys as bare spheres, so each carries its word and the chosen one a ring.
  const [chosen, setChosen] = useState<{ metal?: string; stone?: string }>({});

  useEffect(() => {
    if (!on) return;
    document.body.dataset.present = "1";
    entered.current = false;
    void Promise.resolve(client.ring.setAutoRotate(true, 0.5)).catch(() => undefined);
    const el = document.documentElement;
    if (!document.fullscreenElement && el.requestFullscreen) {
      el.requestFullscreen()
        .then(() => { entered.current = true; })
        .catch(() => undefined); // full screen refused: present in the window instead
    }
    const onFs = () => {
      if (!document.fullscreenElement && entered.current) patchUi({ present: false });
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") patchUi({ present: false });
    };
    document.addEventListener("fullscreenchange", onFs);
    window.addEventListener("keydown", onKey);
    return () => {
      delete document.body.dataset.present;
      document.removeEventListener("fullscreenchange", onFs);
      window.removeEventListener("keydown", onKey);
      void Promise.resolve(client.ring.setAutoRotate(false)).catch(() => undefined);
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    };
  }, [on, client, patchUi]);

  if (!on) return null;

  const hasMetal = groups.some((g) => g.kind === "metal");
  const hasGems = groups.some((g) => g.kind === "gem");
  const fail = (e: Error) => notice(`Couldn't change that: ${e.message}`);
  const pick = (label: string, fill: string, active: boolean, onPick: () => void) => (
    <button
      key={label}
      onClick={onPick}
      aria-pressed={active}
      className="group flex w-[70px] flex-col items-center gap-1"
      data-present-choice={label}
    >
      <span
        className="h-9 w-9 rounded-full transition-transform group-hover:scale-110"
        style={{ background: fill, boxShadow: active ? "0 0 0 2px #fff, 0 0 0 3.5px #946f2e" : "inset 0 0 0 1px rgba(0,0,0,0.12)" }}
      />
      <span className={`whitespace-nowrap text-[11px] leading-[13px] ${active ? "font-semibold text-ed-text" : "text-ed-text-2"}`}>{label}</span>
    </button>
  );

  return (
    <div className="pointer-events-none absolute inset-0 z-[120]" data-present-overlay>
      <button
        onClick={() => patchUi({ present: false })}
        className="pointer-events-auto absolute right-5 top-5 inline-flex h-9 items-center gap-1.5 rounded-full bg-white/90 px-4 text-[13px] font-medium text-ed-text shadow-[0_6px_20px_rgba(0,0,0,0.12)] backdrop-blur hover:bg-white"
        data-present-exit
      >
        <Icon name="close" size={18} />
        Exit
      </button>
      {hasMetal || hasGems ? (
        <div className="pointer-events-auto absolute bottom-6 left-1/2 flex -translate-x-1/2 items-end gap-1 rounded-2xl bg-white/90 px-3 py-2.5 shadow-[0_6px_20px_rgba(0,0,0,0.12)] backdrop-blur">
          {hasMetal
            ? catalogs.metalPresets.map((m) =>
                pick(m.label, metalFill(m.color), chosen.metal === m.id, () => {
                  setChosen((c) => ({ ...c, metal: m.id }));
                  void engine.applyPreset("metal", { presetId: m.id, finishId: "polished" }, `Metal: ${m.label}`).catch(fail);
                }),
              )
            : null}
          {hasMetal && hasGems ? <span className="mx-1 mb-5 h-8 w-px bg-black/10" /> : null}
          {hasGems
            ? STONES.filter((s) => catalogs.gemPresets.some((g) => g.id === s.id)).map((s) =>
                pick(s.label, s.fill, chosen.stone === s.id, () => {
                  setChosen((c) => ({ ...c, stone: s.id }));
                  void engine.applyPreset("gem", { presetId: s.id }, `Stone: ${s.label}`).catch(fail);
                }),
              )
            : null}
        </div>
      ) : null}
    </div>
  );
}
